import React, { useState, useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { collection, getDocs, limit, orderBy, query, where } from "@firebase/firestore";
import { db } from "../../firebase/FirebaseProvider";
import { User, Session, Photo } from "../types/Types";
import { logDataLoaded } from "../util/Util";
import PhotoRow from "../locations/profile/PhotoRow";
import { Link } from "react-router-dom";
import {V2GlassPanel} from "../shared/v2/V2Glass";
import V2PhotoRow from "../shared/v2/V2PhotoRow";

interface Props {
    usersMap: Map<string, User>;
    sessionsMap: Map<string, Session>;
    currentUser: User | undefined;
    likesMap: Map<string, boolean>;
    isLoading?: boolean;
}

const MostLikedPhotos: React.FC<Props> = ({ usersMap, sessionsMap, currentUser, likesMap, isLoading = false }) => {
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            await loadMostLikedPhotos();
        })();
    }, []);

    const loadMostLikedPhotos = async () => {
        try {
            setLoading(true);
            const photosQuery = query(
                collection(db, 'photos'),
                where('hidden', '==', false),
                orderBy('likeCount', 'desc'),
                orderBy('createdAt', 'desc'),
                limit(6)
            );
            const photosSnapshot = await getDocs(photosQuery);
            const photosData: Photo[] = [];
            photosSnapshot.forEach((docSnap) => {
                const data = docSnap.data() as Photo;
                data.id = docSnap.id;
                photosData.push(data);
            });

            logDataLoaded('most liked photos', photosData.length);
            setPhotos(photosData);
        } catch (error) {
            console.error("Error loading most liked photos:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || isLoading) {
        if (currentUser?.enableUiV2) {
            return (
                <V2GlassPanel className="v2-hub-photo-section v2-glass-list-panel">
                    <div className="v2-hub-photo-section-header">
                        <b>Top Liked Photos</b>
                        <Link to="/photos/all?sort=likes">View all</Link>
                    </div>
                    <div className="v2-hub-photo-loading">Loading most liked photos...</div>
                </V2GlassPanel>
            );
        }

        return (
            <Card className={"mb-3"}>
                <Card.Header>
                    <Row>
                        <Col>
                            <div><b>Top Liked Photos</b></div>
                        </Col>
                        <Col xs="auto">
                            <small><Link to="/photos/all?sort=likes">View all</Link></small>
                        </Col>
                    </Row>
                </Card.Header>
                <div className={'loading'}>Loading most liked photos...</div>
            </Card>
        );
    }

    if (photos.length === 0 || !currentUser) {
        return null;
    }

    if (currentUser.enableUiV2) {
        return (
            <V2GlassPanel className="v2-hub-photo-section v2-glass-list-panel">
                <div className="v2-hub-photo-section-header">
                    <b>Top Liked Photos</b>
                    <Link to="/photos/all?sort=likes">View all</Link>
                </div>
                <div className="v2-hub-photo-row-list">
                    {photos.map((photo, index) => (
                        <V2PhotoRow
                            key={photo.id + index}
                            currentUser={currentUser}
                            photo={photo}
                            photoUser={usersMap.get(photo.userId)}
                            photoSession={sessionsMap.get(photo.userId)}
                            likesMap={likesMap}
                        />
                    ))}
                </div>
            </V2GlassPanel>
        );
    }

    return (
        <Card className={"mb-3"}>
            <Card.Header>
                <Row>
                    <Col>
                        <div><b>Top Liked Photos</b></div>
                    </Col>
                    <Col xs="auto">
                        <small><Link to="/photos/all?sort=likes">View all</Link></small>
                    </Col>
                </Row>
            </Card.Header>
            <div>
                <div className={"album"}>
                    {photos.map((photo: Photo, index: number) => (
                        <PhotoRow
                            key={photo.id + index}
                            currentUser={currentUser}
                            photo={photo}
                            photoUser={usersMap.get(photo.userId)}
                            displayLocation={true}
                            displayEditButton={true}
                            likesMap={likesMap}
                        />
                    ))}
                </div>
            </div>
        </Card>
    );
};

export default MostLikedPhotos;
